import {ref, type Ref} from 'vue'
import {defineStore} from 'pinia'
import type {RouteLocationRaw} from "vue-router";
import {RouteEnum} from "@/router/RouteEnum.ts";
import router from "@/router/router.ts";
import {createRemoteJWKSet, jwtVerify} from 'jose'
import axios from 'axios'

const issuer = import.meta.env.VITE_AUTH_ISSUER
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID
const redirectUri = import.meta.env.VITE_OIDC_REDIRECT_URI || `${window.location.origin}/auth/callback`
const transactionKey = 'oidc_transaction'
// OIDC requests must not carry business API tokens or use its error interceptors.
const oidcHttp = axios.create({timeout: 15000})

type OidcTransaction = { state: string; nonce: string; verifier: string; returnTo: string | null }
type TokenResponse = { access_token: string; id_token: string; token_type: string; expires_in: number }

function randomValue(bytes = 32): string {
  const value = crypto.getRandomValues(new Uint8Array(bytes))
  return btoa(String.fromCharCode(...value)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export const useAccountStore = defineStore('account', () => {
  const token: Ref<null | string> = ref(null)
  const expiresAt: Ref<number> = ref(0)
  const originRoute: Ref<null | RouteLocationRaw> = ref(null)
  let connectionStarted = false

  function isLogged(): boolean {
    return null !== token.value && expiresAt.value > Date.now()
  }

  function logout(): void {
    token.value = null
    expiresAt.value = 0
    connectionStarted = false
  }

  async function connect() {
    if (connectionStarted) return
    connectionStarted = true
    if (isLogged()) {
      redirectToLoggedRoute()
      return
    }

    try {
      if (!issuer || !clientId) throw new Error('Configuration OIDC manquante')
      const transaction: OidcTransaction = {
        state: randomValue(), nonce: randomValue(), verifier: randomValue(48),
        returnTo: originRoute.value ? router.resolve(originRoute.value).fullPath : null
      }
      sessionStorage.setItem(transactionKey, JSON.stringify(transaction))
      const challenge = await sha256Base64Url(transaction.verifier)
      const discovery = await oidcHttp.get(`${issuer}/.well-known/openid-configuration`)
      const authorization = new URL(discovery.data.authorization_endpoint)
      authorization.search = new URLSearchParams({
        response_type: 'code', client_id: clientId, redirect_uri: redirectUri,
        scope: 'openid profile email', state: transaction.state, nonce: transaction.nonce,
        code_challenge: challenge, code_challenge_method: 'S256'
      }).toString()
      window.location.assign(authorization.toString())
    } catch (error) {
      connectionStarted = false
      sessionStorage.removeItem(transactionKey)
      throw error
    }
  }

  async function handleCallback(code: string | null, state: string | null, error: string | null) {
    const callbackUrl = new URL(window.location.href)
    history.replaceState({}, document.title, callbackUrl.pathname)
    const serialized = sessionStorage.getItem(transactionKey)
    sessionStorage.removeItem(transactionKey)
    if (!serialized) throw new Error('Tentative de connexion absente')
    const transaction = JSON.parse(serialized) as OidcTransaction
    if (!state || state !== transaction.state) throw new Error('État OAuth invalide')
    if (error || !code) throw new Error('La connexion a été refusée')
    if (!issuer || !clientId) throw new Error('Configuration OIDC manquante')
    if (transaction.returnTo) originRoute.value = transaction.returnTo

    const discovery = await oidcHttp.get(`${issuer}/.well-known/openid-configuration`)
    const body = new URLSearchParams({
      grant_type: 'authorization_code', client_id: clientId, redirect_uri: redirectUri,
      code, code_verifier: transaction.verifier
    })
    const response = await oidcHttp.post<TokenResponse>(discovery.data.token_endpoint, body, {
      headers: {'Content-Type': 'application/x-www-form-urlencoded'}
    })
    if (!response.data.access_token || !response.data.id_token || response.data.token_type.toLowerCase() !== 'bearer') {
      throw new Error('Réponse OIDC invalide')
    }
    const jwks = createRemoteJWKSet(new URL(discovery.data.jwks_uri))
    const {payload} = await jwtVerify(response.data.id_token, jwks, {issuer, audience: clientId, algorithms: ['RS256']})
    if (payload.nonce !== transaction.nonce) throw new Error('Nonce OIDC invalide')
    token.value = response.data.access_token
    if (!Number.isFinite(response.data.expires_in) || response.data.expires_in <= 0) {
      logout()
      throw new Error('Durée du token invalide')
    }
    expiresAt.value = Date.now() + response.data.expires_in * 1000
    redirectToLoggedRoute()
  }

  function redirectToLoggedRoute() {
    if (null === originRoute.value) {
      router.push({name: RouteEnum.Dashboard}).then()
    } else {
      const origin = originRoute.value
      originRoute.value = null
      router.push(origin).then()
    }
  }

  return {token, isLogged, logout, connect, handleCallback, originRoute}
})
